import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2, Sparkles, ExternalLink, MessageSquare, Bell } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { LiveChart } from "@/components/LiveChart";
import { SignalReport } from "@/components/SignalReport";
import { BeginnerNote } from "@/components/Beginner";
import { useLiveTicker } from "@/hooks/useLiveTicker";
import { useLiveQuotes } from "@/context/LiveMarketContext";
import { useMode } from "@/context/ModeContext";

export const ASSET_KEY = "evidex.trade.asset";
export const DEFAULT_ASSET = { id: "bitcoin", symbol: "BTC", name: "Bitcoin" };
export const loadAsset = () => { try { const a = JSON.parse(localStorage.getItem(ASSET_KEY) || "null"); return a?.id && a?.symbol ? a : DEFAULT_ASSET; } catch { return DEFAULT_ASSET; } };

export const Section = ({ title, step, hint, children, tid }) => (
  <section className="apx-card p-4 space-y-3" data-testid={tid}>
    <div className="flex items-baseline gap-2"><span className="text-[10px] font-mono-ticker text-cyan-300">{step}</span><h2 className="font-display font-semibold text-base">{title}</h2>{hint && <span className="text-xs text-slate-500 hidden sm:inline">· {hint}</span>}</div>
    {children}
  </section>
);

// Search any asset in the local index; selecting it drives chart, ticket, research and predictions below.
export const AssetPicker = ({ asset, onPick }) => {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const t = useRef(null);
  useEffect(() => {
    clearTimeout(t.current);
    if (!q.trim()) { setHits([]); return undefined; }
    t.current = setTimeout(async () => {
      setBusy(true);
      try { setHits(((await api.get("/market/search", { params: { q: q.trim() } })).data.coins || []).slice(0, 8)); setOpen(true); }
      catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    }, 250);
    return () => clearTimeout(t.current);
  }, [q]);
  const pick = (c) => { onPick({ id: c.id, symbol: c.symbol, name: c.name, ai: c.ai_predictions, live: !!c.live_venue }); setQ(""); setHits([]); setOpen(false); };
  return (
    <div className="relative" data-testid="trade-asset-picker">
      <label className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/60 px-3 py-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : <Search className="w-4 h-4 text-slate-400" />}
        <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => hits.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
               placeholder={`Search a crypto to trade on paper — currently ${asset.name} (${asset.symbol})`} className="bg-transparent outline-none text-sm flex-1 text-slate-100" data-testid="trade-asset-search" />
        <span className="text-[10px] font-mono-ticker text-slate-500 hidden sm:inline" data-testid="trade-asset-current">{asset.symbol}</span>
      </label>
      {open && hits.length > 0 && (
        <div className="absolute z-30 mt-1 w-full rounded-xl border border-slate-800 bg-[#0F141C] shadow-xl overflow-hidden" data-testid="trade-asset-results">
          {hits.map((c) => (
            <button key={c.id} type="button" onMouseDown={() => pick(c)} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-800/50" data-testid={`trade-asset-hit-${c.id}`}>
              {c.image && <img src={c.image} alt="" className="w-5 h-5 rounded-full" />}
              <span className="text-sm">{c.name}</span><span className="text-[10px] font-mono-ticker text-slate-500">{c.symbol}</span>
              <span className="ml-auto text-[10px] font-mono-ticker text-slate-500">{c.live_venue ? "live feed" : "polled"}{c.ai_predictions ? " · AI predictions" : ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const WorkspaceChart = ({ asset }) => {
  const [mode, setMode] = useState("candles");
  const [interval, setIv] = useState(1);
  const live = useLiveTicker(asset.symbol, true, interval);
  return (
    <>
      <LiveChart live={live} symbol={asset.symbol} mode={mode} onMode={setMode} interval={interval} onInterval={setIv} />
      <BeginnerNote testid="trade-chart-note"><strong className="text-slate-300">Reading this chart</strong> — it is the real market price of {asset.symbol}, updating as trades print. Green candles closed higher than they opened, red lower. If the badge says QUIET or STALE, the last price is old — check the Live Markets page before trading on it.</BeginnerNote>
    </>
  );
};

export const WorkspaceResearch = ({ asset, onPaperTrade }) => {
  const { beginner } = useMode();
  const [signal, setSignal] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setSignal(null);
    api.get(`/coin/${asset.id}/signal`, { params: { auto: true } }).then((r) => setSignal(r.data || null)).catch(() => {});
  }, [asset.id]);
  const run = async () => {
    if (loading) return;
    setLoading(true);
    try { setSignal((await api.get(`/coin/${asset.id}/signal`, { params: { force: true } })).data); toast.success("Fresh AI analysis ready"); window.dispatchEvent(new Event("apx:billing-changed")); }
    catch (e) { if (e?.response?.status !== 402) toast.error(apiError(e, "Analysis failed")); }
    setLoading(false);
  };
  return (
    <div className="space-y-3">
      <BeginnerNote testid="trade-research-note"><strong className="text-slate-300">What this is</strong> — EV-IDEX's plain-English read of {asset.name}: the outlook, the evidence for and against, and the risk. It is research to help you think, not a recommendation to trade.</BeginnerNote>
      <SignalReport signal={signal} loading={loading} onRefresh={run} onPaperTrade={onPaperTrade} />
      <div className="flex flex-wrap gap-2 text-xs">
        <Link to={`/coin/${asset.id}`} className="inline-flex items-center gap-1 text-cyan-300 hover:underline" data-testid="trade-open-coin-page"><ExternalLink className="w-3 h-3" /> Full {asset.name} page{beginner ? "" : " (indicators, order book, brackets)"}</Link>
        <Link to="/chat" className="inline-flex items-center gap-1 text-cyan-300 hover:underline" data-testid="trade-ask-ai"><MessageSquare className="w-3 h-3" /> Ask AI Research about {asset.symbol}</Link>
      </div>
    </div>
  );
};

const Outcome = ({ r }) => {
  const o = r.outcome || r.status;
  const cls = o === "correct" ? "text-emerald-300" : o === "incorrect" ? "text-rose-300" : o === "neutral" ? "text-slate-300" : "text-amber-200";
  return <span className={`font-mono-ticker text-xs ${cls}`}>{o}</span>;
};

export const WorkspacePredictions = ({ asset }) => {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/predictions/ledger", { params: { asset: asset.id, limit: 8 } }).then((r) => setD(r.data)).catch(() => setD({ rows: [], error: true })); }, [asset.id]);
  const rows = d?.rows || [];
  const counts = useMemo(() => d?.counts?.outcome || {}, [d]);
  return (
    <div className="space-y-3">
      <BeginnerNote testid="trade-predictions-note"><strong className="text-slate-300">What this is</strong> — the sealed record of EV-IDEX's own past calls on {asset.symbol}. Each was locked when made and graded later against the real price, so you can see how often it was right before you lean on today's outlook.</BeginnerNote>
      {!d && <div className="text-xs text-slate-500 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading ledger…</div>}
      {d && rows.length === 0 && <div className="text-xs text-slate-500" data-testid="trade-predictions-empty"><Sparkles className="w-3.5 h-3.5 inline mr-1 text-cyan-300" />{d.error ? "Ledger unavailable right now." : `No sealed predictions for ${asset.name} yet — it is outside the daily prediction universe, so only price data is shown. Nothing is invented.`}</div>}
      {rows.length > 0 && (
        <div className="tbl-scroll overflow-x-auto" data-testid="trade-predictions-table">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800"><th className="py-1.5 pr-3">When (UTC)</th><th className="py-1.5 pr-3">Timeframe</th><th className="py-1.5 pr-3">Call</th><th className="py-1.5 pr-3">Result</th></tr></thead>
            <tbody>{rows.map((r) => <tr key={r.id} className="border-b border-slate-800/60" data-testid={`trade-pred-${r.id}`}><td className="py-1.5 pr-3 text-xs text-slate-400">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td><td className="py-1.5 pr-3 text-xs">{r.timeframe}</td><td className="py-1.5 pr-3 text-xs font-semibold">{r.prediction}</td><td className="py-1.5 pr-3"><Outcome r={r} /></td></tr>)}</tbody>
          </table>
        </div>
      )}
      <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3" data-testid="trade-predictions-foot">
        {Object.keys(counts).length > 0 && <span>Graded so far: {Object.entries(counts).map(([k, n]) => `${n} ${k}`).join(" · ")}</span>}
        <Link to={`/performance?tab=score`} className="text-cyan-300 hover:underline">Full accuracy score & ledger →</Link>
      </div>
    </div>
  );
};

const USER_KINDS = ["price_above", "price_below", "range_enter", "range_exit", "sr_break", "volume_spike", "volatility", "signal_change"];
// Quick price alert for the selected asset, set without leaving the workspace. Lists this asset's active alerts.
export const WorkspaceAlerts = ({ asset }) => {
  const { quotes } = useLiveQuotes(useMemo(() => [{ symbol: asset.symbol, id: asset.id }], [asset.symbol, asset.id]));
  const price = quotes[asset.symbol]?.price ?? null;
  const [kind, setKind] = useState("price_above");
  const [threshold, setThreshold] = useState("");
  const [alerts, setAlerts] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/alerts").then((r) => setAlerts((Array.isArray(r.data) ? r.data : r.data.alerts || []).filter((a) => a.coin_id === asset.id && USER_KINDS.includes(a.kind) && a.status === "active"))).catch(() => setAlerts([])), [asset.id]);
  useEffect(() => { setAlerts(null); load(); }, [load]);
  useEffect(() => { setThreshold(""); }, [asset.id]);
  const create = async () => {
    const t = Number(threshold);
    if (!t || t <= 0) { toast.error("Enter a price"); return; }
    setBusy(true);
    try {
      await api.post("/alerts", { coin_id: asset.id, symbol: asset.symbol, name: asset.name, kind, threshold: t, expires_days: 30, note: "Set from the paper-trading workspace" });
      toast.success(`Alert set: ${asset.symbol} ${kind === "price_above" ? "above" : "below"} $${t.toLocaleString()}`);
      setThreshold(""); load();
    } catch (e) { toast.error(apiError(e, "Could not create alert")); }
    setBusy(false);
  };
  const remove = async (id) => { try { await api.delete(`/alerts/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };
  const fmt = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: n < 1 ? 6 : 2 })}`);
  return (
    <div className="space-y-3" data-testid="trade-alerts">
      <BeginnerNote testid="trade-alerts-note"><strong className="text-slate-300">Price alerts</strong> — EV-IDEX watches {asset.symbol} for you and sends an in-app (and push, if connected) notification when it crosses your price. Alerts never place trades.</BeginnerNote>
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <span className="text-xs text-slate-400 shrink-0">Notify me when {asset.symbol} goes</span>
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="apx-ring rounded-lg bg-slate-900 border border-slate-800 text-sm px-2 py-1.5" data-testid="trade-alert-kind">
          <option value="price_above">above</option><option value="price_below">below</option>
        </select>
        <input value={threshold} onChange={(e) => setThreshold(e.target.value)} inputMode="decimal" placeholder={price != null ? `e.g. ${fmt(kind === "price_above" ? price * 1.05 : price * 0.95)}` : "price in USD"}
               className="apx-ring rounded-lg bg-slate-900 border border-slate-800 text-sm px-3 py-1.5 font-mono-ticker w-40" data-testid="trade-alert-price" />
        {price != null && <button type="button" onClick={() => setThreshold(String(Number((kind === "price_above" ? price * 1.05 : price * 0.95).toPrecision(5))))} className="text-[11px] text-cyan-300 hover:underline" data-testid="trade-alert-suggest">use ±5% of {fmt(price)}</button>}
        <button type="button" onClick={create} disabled={busy} className="rounded-lg px-3 py-1.5 text-xs font-semibold btn-primary disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="trade-alert-create">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />} Set alert</button>
      </div>
      {alerts === null && <div className="text-xs text-slate-500">Loading your {asset.symbol} alerts…</div>}
      {alerts && alerts.length === 0 && <div className="text-xs text-slate-500" data-testid="trade-alerts-empty">No active alerts on {asset.symbol} yet.</div>}
      {alerts && alerts.length > 0 && (
        <ul className="divide-y divide-slate-800/70 text-sm" data-testid="trade-alerts-list">
          {alerts.map((a) => (
            <li key={a.id} className="py-1.5 flex items-center justify-between gap-3" data-testid={`trade-alert-${a.id}`}>
              <span className="text-xs text-slate-200">{a.condition || a.kind}{a.expires_at ? <span className="text-slate-500"> · until {a.expires_at.slice(0, 10)}</span> : null}</span>
              <button type="button" onClick={() => remove(a.id)} className="text-[11px] text-slate-400 hover:text-rose-300" data-testid={`trade-alert-remove-${a.id}`}>Remove</button>
            </li>
          ))}
        </ul>
      )}
      <div className="text-[11px] text-slate-500">Need range, breakout, volume or signal-change alerts? <Link to="/alerts" className="text-cyan-300 hover:underline" data-testid="trade-alerts-more">Open Alerts</Link> — your {asset.symbol} alerts also appear there.</div>
    </div>
  );
};
